import type { ActivityResourceType } from '@data-hub/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { ActivityFiltersBar } from '@/features/activity/ActivityFilters';
import { ActivityTimeline } from '@/features/activity/ActivityTimeline';
import { useActivityList } from '@/features/activity/use-activity';

const activitySearchSchema = z.object({
  type: z.enum(['resource', 'category', 'user', 'announcement']).optional(),
});

export const Route = createFileRoute('/_authed/activity')({
  validateSearch: activitySearchSchema,
  component: ActivityPage,
});

function ActivityPage() {
  const { type } = Route.useSearch();
  const navigate = useNavigate();
  const query = useActivityList({ resourceType: type });

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data?.pages]);

  const setResourceType = (next: ActivityResourceType | undefined) => {
    void navigate({ to: '/activity', search: { type: next } });
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Activity</h1>
        <p className="text-sm text-muted-foreground">
          Who created, edited, or shared what across the hub.
        </p>
      </div>

      <ActivityFiltersBar resourceType={type} onResourceTypeChange={setResourceType} />

      <ActivityTimeline
        items={items}
        isPending={query.isPending}
        isError={query.isError}
        errorMessage={query.error?.message}
        onRetry={() => void query.refetch()}
        hasMore={!!query.hasNextPage}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
      />
    </div>
  );
}
